import { createContext, useContext, useMemo, useEffect } from 'react';

import { useBankingDataContext } from '#client/BankingDataContext';
import { useLogError } from '#client/hooks/useLogError';
import useToastContext from '#client/hooks/useToastContext';

export const UnitAccountContext = createContext();
UnitAccountContext.displayName = 'UnitAccountContext';

const useUnitAccount = () => {
  const context = useContext(UnitAccountContext);

  if (typeof context === 'undefined') {
    throw new Error('useUnitAccount must be used within a UnitAccountContext.Provider');
  }

  return context;
};

export const is404Error = (error) => {
  const statusCode = error?.statusCode;
  return statusCode === 404;
};

export function UnitAccountProvider({ children }) {
  const { createCommonErrorToast } = useToastContext();

  const { error, errors, loading, data, refetch } = useBankingDataContext();

  useLogError(errors, error);

  const value = useMemo(
    () => ({
      error,
      loading,
      unitAccount: data?.bankAccount ?? null,
      refetch
    }),
    [error, loading, data?.bankAccount, refetch]
  );

  useEffect(() => {
    if (error && !is404Error(error)) {
      createCommonErrorToast({ error });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return <UnitAccountContext.Provider value={value}>{children}</UnitAccountContext.Provider>;
}

export default useUnitAccount;
